import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { ROOM_NUMBERING_METHODS } from "@multiconsult-group/multiplacer-core";
import { useEffect, useMemo, useState } from "react";
import { FaCheck, FaFloppyDisk, FaRotateLeft, FaXmark } from "react-icons/fa6";
import {
  useDeleteMultiplacerRoomsRoomNumberingApprovalMutation,
  useDeleteMultiplacerRoomsRoomNumberingManualValueMutation,
  usePostMultiplacerRoomsRoomNumberingApprovalMutation,
  usePutMultiplacerRoomsRoomNumberingEvaluationMutation,
  usePutMultiplacerRoomsRoomNumberingManualValueMutation,
  type PutMultiplacerRoomsRoomNumberingEvaluationApiArg,
} from "../../openapi";
import { useAppSelector } from "../app/hooks";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";

type RoomNumberingEvaluationStatus =
  PutMultiplacerRoomsRoomNumberingEvaluationApiArg["body"]["status"];

const roomNumberingEvaluationStatusOptions: {
  value: RoomNumberingEvaluationStatus;
  label: string;
}[] = [
  { value: "NOT_REVIEWED", label: "Ikke vurdert" },
  { value: "CORRECT", label: "Riktig" },
  { value: "INCORRECT", label: "Feil" },
  { value: "PARTLY_CORRECT", label: "Delvis riktig" },
  { value: "UNCLEAR", label: "Usikkert" },
];

const formatDateTime = (value: string | Date | undefined): string => {
  if (value == null) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const InfoLine: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="sm" ta="right">
      {value || "-"}
    </Text>
  </Group>
);

export const RoomNumberingDetailsPanel: React.FC = () => {
  const room = useAppSelector(selectHttpSingleRoomFull);
  const roomNumbering = room?.roomNumbering;
  const method = ROOM_NUMBERING_METHODS.find((item) => item.key === roomNumbering?.run?.methodKey);
  const displayedValue = roomNumbering?.value ?? roomNumbering?.suggested ?? "";
  const [manualValue, setManualValue] = useState("");
  const [evaluationStatus, setEvaluationStatus] =
    useState<RoomNumberingEvaluationStatus>("NOT_REVIEWED");
  const [evaluationExpectedValue, setEvaluationExpectedValue] = useState("");
  const [evaluationComment, setEvaluationComment] = useState("");
  const [setManualValueMutation, setManualValueState] =
    usePutMultiplacerRoomsRoomNumberingManualValueMutation();
  const [clearManualValueMutation, clearManualValueState] =
    useDeleteMultiplacerRoomsRoomNumberingManualValueMutation();
  const [approveMutation, approveState] = usePostMultiplacerRoomsRoomNumberingApprovalMutation();
  const [clearApprovalMutation, clearApprovalState] =
    useDeleteMultiplacerRoomsRoomNumberingApprovalMutation();
  const [setEvaluationMutation, setEvaluationState] =
    usePutMultiplacerRoomsRoomNumberingEvaluationMutation();

  useEffect(() => {
    setManualValue(roomNumbering?.value ?? "");
  }, [room?.roomNumber, roomNumbering?.value]);

  useEffect(() => {
    setEvaluationStatus(roomNumbering?.evaluation?.status ?? "NOT_REVIEWED");
    setEvaluationExpectedValue(roomNumbering?.evaluation?.expectedValue ?? "");
    setEvaluationComment(roomNumbering?.evaluation?.comment ?? "");
  }, [
    room?.roomNumber,
    roomNumbering?.evaluation?.comment,
    roomNumbering?.evaluation?.expectedValue,
    roomNumbering?.evaluation?.status,
  ]);

  const trimmedManualValue = manualValue.trim();
  const trimmedEvaluationExpectedValue = evaluationExpectedValue.trim();
  const trimmedEvaluationComment = evaluationComment.trim();
  const isManualValueDirty = trimmedManualValue !== (roomNumbering?.value ?? "");
  const isEvaluationDirty =
    evaluationStatus !== (roomNumbering?.evaluation?.status ?? "NOT_REVIEWED") ||
    trimmedEvaluationExpectedValue !== (roomNumbering?.evaluation?.expectedValue ?? "") ||
    trimmedEvaluationComment !== (roomNumbering?.evaluation?.comment ?? "");
  const canApprove = useMemo(() => {
    const value = displayedValue.trim();
    return value.length > 0 && value !== "-";
  }, [displayedValue]);

  const roomInput =
    room == null
      ? null
      : {
          projectNo: room.projectNo,
          roomNumber: room.roomNumber,
        };

  const isSaving =
    setManualValueState.isLoading ||
    clearManualValueState.isLoading ||
    approveState.isLoading ||
    clearApprovalState.isLoading ||
    setEvaluationState.isLoading;

  const handleSaveManualValue = async () => {
    if (!roomInput || trimmedManualValue.length === 0) return;

    await setManualValueMutation({
      body: {
        ...roomInput,
        value: trimmedManualValue,
      },
    }).unwrap();
  };

  const handleClearManualValue = async () => {
    if (!roomInput) return;

    await clearManualValueMutation(roomInput).unwrap();
  };

  const handleApprove = async () => {
    if (!roomInput || !canApprove) return;

    // Lock in the current displayed number as a manual value before approving,
    // so the approved number is stable even if re-calculation would produce a different result.
    const valueToLock = displayedValue.trim();
    if (valueToLock && valueToLock !== roomNumbering?.value?.trim()) {
      await setManualValueMutation({ body: { ...roomInput, value: valueToLock } }).unwrap();
      setManualValue(valueToLock);
    }

    await approveMutation({ body: roomInput }).unwrap();
  };

  const handleClearApproval = async () => {
    if (!roomInput) return;

    await clearApprovalMutation(roomInput).unwrap();
  };

  const handleSaveEvaluation = async () => {
    if (!roomInput) return;

    await setEvaluationMutation({
      body: {
        ...roomInput,
        status: evaluationStatus,
        ...(trimmedEvaluationExpectedValue ? { expectedValue: trimmedEvaluationExpectedValue } : {}),
        ...(trimmedEvaluationComment ? { comment: trimmedEvaluationComment } : {}),
      },
    }).unwrap();
  };

  return (
    <Card withBorder radius="md" m="sm">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Romnummerering</Text>
            <Text size="sm" c="dimmed">
              {room?.roomNumber ?? "-"} {room?.roomName ?? ""}
            </Text>
          </div>
          {roomNumbering?.approval != null ? (
            <Badge color="green">Godkjent</Badge>
          ) : (
            <Badge color={canApprove ? "yellow" : "red"} variant="light">
              Ikke godkjent
            </Badge>
          )}
        </Group>

        <Stack gap="xs">
          <InfoLine label="Foreslått romnummer" value={roomNumbering?.suggested} />
          <InfoLine label="Gjeldende verdi" value={displayedValue} />
          <InfoLine label="Manuelt endret av" value={roomNumbering?.updatedBy?.name} />
          <InfoLine label="Manuelt endret" value={formatDateTime(roomNumbering?.updatedAt)} />
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Manuell verdi
          </Text>
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <TextInput
              value={manualValue}
              onChange={(event) => setManualValue(event.currentTarget.value)}
              placeholder={roomNumbering?.suggested ?? "Romnummer"}
              size="xs"
              style={{ flex: 1 }}
              disabled={room == null || isSaving}
            />
            <Tooltip label="Lagre manuell verdi">
              <ActionIcon
                variant="filled"
                color="blue"
                size="lg"
                disabled={!isManualValueDirty || trimmedManualValue.length === 0 || isSaving}
                loading={setManualValueState.isLoading}
                onClick={handleSaveManualValue}
              >
                <FaFloppyDisk size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Fjern manuell verdi">
              <ActionIcon
                variant="light"
                color="gray"
                size="lg"
                disabled={roomNumbering?.value == null || isSaving}
                loading={clearManualValueState.isLoading}
                onClick={handleClearManualValue}
              >
                <FaRotateLeft size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Stack>

        <Group gap="xs" grow>
          {roomNumbering?.approval == null ? (
            <Button
              leftSection={<FaCheck size={14} />}
              size="xs"
              disabled={!canApprove || room == null || isSaving}
              loading={approveState.isLoading}
              onClick={handleApprove}
            >
              Godkjenn
            </Button>
          ) : (
            <Button
              leftSection={<FaXmark size={14} />}
              variant="light"
              color="red"
              size="xs"
              disabled={room == null || isSaving}
              loading={clearApprovalState.isLoading}
              onClick={handleClearApproval}
            >
              Fjern godkjenning
            </Button>
          )}
        </Group>

        <Divider />

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Beregning
          </Text>
          <InfoLine label="Kjørt av" value={roomNumbering?.run?.runBy.name} />
          <InfoLine label="Kjørt" value={formatDateTime(roomNumbering?.run?.runAt)} />
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Godkjenning
          </Text>
          <InfoLine label="Godkjent av" value={roomNumbering?.approval?.approvedBy.name} />
          <InfoLine label="Godkjent" value={formatDateTime(roomNumbering?.approval?.approvedAt)} />
        </Stack>

        <Divider />

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Algoritmetest
          </Text>
          <Select
            label="Vurdering"
            data={roomNumberingEvaluationStatusOptions}
            value={evaluationStatus}
            onChange={(value) =>
              setEvaluationStatus((value ?? "NOT_REVIEWED") as RoomNumberingEvaluationStatus)
            }
            allowDeselect={false}
            size="xs"
            disabled={room == null || isSaving}
          />
          <TextInput
            label="Forventet verdi"
            value={evaluationExpectedValue}
            onChange={(event) => setEvaluationExpectedValue(event.currentTarget.value)}
            placeholder={displayedValue || roomNumbering?.suggested || "Romnummer"}
            size="xs"
            disabled={room == null || isSaving}
          />
          <Textarea
            label="Kommentar"
            value={evaluationComment}
            onChange={(event) => setEvaluationComment(event.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={5}
            maxLength={2000}
            size="xs"
            disabled={room == null || isSaving}
          />
          <Button
            leftSection={<FaFloppyDisk size={14} />}
            size="xs"
            variant="light"
            disabled={room == null || !isEvaluationDirty || isSaving}
            loading={setEvaluationState.isLoading}
            onClick={handleSaveEvaluation}
          >
            Lagre vurdering
          </Button>
          <Stack gap={2}>
            <InfoLine label="Vurdert av" value={roomNumbering?.evaluation?.reviewedBy?.name} />
            <InfoLine
              label="Vurdert"
              value={formatDateTime(roomNumbering?.evaluation?.reviewedAt)}
            />
          </Stack>
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Metode
          </Text>
          <InfoLine label="Tittel" value={method?.title} />
          <InfoLine label="Kunde" value={method?.customer} />
          <InfoLine label="PDF" value={method?.pdfFileName} />
          {method?.summary != null && (
            <Text size="sm" c="dimmed">
              {method.summary}
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
